import { buildSearchContextSummary } from "./searchContextSummary";
import { capMatchPercent } from "./matchScoreUtils";
import { hasMatchIntent } from "./resolveMatchIntent";

export const SIMILAR_PRODUCTS_SOURCES = {
  AI_SESSION: "ai_session",
  INTENT_ENGINE: "intent_engine",
  CATEGORY_BROWSE: "category_browse",
};

const getCategoryId = (product) => {
  if (!product) return null;
  const cat = product.category;
  if (!cat) return null;
  return String(typeof cat === "object" ? cat._id || cat.id : cat);
};

const getProductId = (product) => {
  if (!product) return null;
  return String(product._id || product.id);
};

const isProductInAiSession = (productId, aiSession) => {
  if (!productId || !aiSession?.products?.length) return false;
  return aiSession.products.some((p) => getProductId(p) === productId);
};

const wizardAnswersToSearchParams = (wizardAnswers) => {
  if (!wizardAnswers || typeof wizardAnswers !== "object") return null;
  return {
    useCase: wizardAnswers.useCase,
    mustHaves: wizardAnswers.mustHaves || [],
    budget: wizardAnswers.budget,
    category: wizardAnswers.type,
  };
};

export const hasSimilarProductsIntent = ({ aiSession, wizardAnswers } = {}) => {
  if (aiSession?.products?.length) return true;
  return hasMatchIntent(wizardAnswers);
};

export const getCurrentProductAiRank = (currentProduct, aiSession) => {
  if (!currentProduct || !aiSession?.products?.length) return null;
  const currentId = getProductId(currentProduct);
  const index = aiSession.products.findIndex(
    (p) => getProductId(p) === currentId,
  );
  if (index === -1) return null;
  const match = aiSession.products[index];
  return match?.rank ?? index + 1;
};

const buildContextSummary = (aiSession, wizardAnswers) => {
  if (aiSession?.searchParams) {
    return buildSearchContextSummary(aiSession.searchParams);
  }
  return buildSearchContextSummary(wizardAnswersToSearchParams(wizardAnswers));
};

const enrichProductForCard = (product, fields) => ({
  ...product,
  matchScore: fields.matchScore ?? product.matchScore ?? null,
  matchExplanation: fields.matchExplanation ?? product.matchExplanation ?? null,
  comparisonLabel: fields.comparisonLabel ?? null,
  rank: fields.rank ?? product.rank ?? null,
  aiRanked: Boolean(fields.aiRanked),
  scoreSource: fields.scoreSource,
});

const shouldIncludeAiSessionProduct = ({
  candidate,
  currentId,
  currentCategoryId,
  currentInSession,
}) => {
  const candidateId = getProductId(candidate);
  if (!candidateId || candidateId === currentId) return false;

  if (currentInSession) {
    return true;
  }

  if (!currentCategoryId) return true;

  const candidateCategoryId = getCategoryId(candidate);
  if (!candidateCategoryId) return false;

  return candidateCategoryId === currentCategoryId;
};

/**
 * Merge AI session, alternatives API, and category browse into one similar-products list.
 */
export function resolveSimilarProducts({
  currentProduct,
  categorySimilar = [],
  alternativesApiResult = null,
  aiSession = null,
  wizardAnswers = null,
  limit = 6,
}) {
  if (!currentProduct) {
    return {
      source: SIMILAR_PRODUCTS_SOURCES.CATEGORY_BROWSE,
      contextSummary: null,
      items: [],
    };
  }

  const currentId = getProductId(currentProduct);
  const currentCategoryId = getCategoryId(currentProduct);
  const currentInSession = isProductInAiSession(currentId, aiSession);
  const hasIntent = hasSimilarProductsIntent({ aiSession, wizardAnswers });
  const contextSummary = hasIntent
    ? buildContextSummary(aiSession, wizardAnswers)
    : null;

  const seen = new Set();
  const items = [];

  const pushItem = (product, fields) => {
    const id = getProductId(product);
    if (!id || id === currentId || seen.has(id)) return;
    seen.add(id);
    items.push({
      product: enrichProductForCard(product, fields),
      matchScore: fields.matchScore ?? null,
      matchExplanation: fields.matchExplanation ?? null,
      comparisonLabel: fields.comparisonLabel ?? null,
      rank: fields.rank ?? null,
      scoreSource: fields.scoreSource,
      inAiSession: fields.scoreSource === SIMILAR_PRODUCTS_SOURCES.AI_SESSION,
      showMatchScore: fields.showMatchScore !== false,
    });
  };

  const aiProducts = (aiSession?.products || [])
    .filter((p) =>
      shouldIncludeAiSessionProduct({
        candidate: p,
        currentId,
        currentCategoryId,
        currentInSession,
      }),
    )
    .sort((a, b) => (a.rank ?? 99) - (b.rank ?? 99));

  aiProducts.forEach((p) => {
    pushItem(p, {
      matchScore:
        p.matchScore != null ? capMatchPercent(p.matchScore) : null,
      matchExplanation: p.matchExplanation || null,
      rank: p.rank ?? null,
      scoreSource: SIMILAR_PRODUCTS_SOURCES.AI_SESSION,
      aiRanked: true,
      showMatchScore: true,
    });
  });

  (alternativesApiResult?.alternatives || []).forEach((entry) => {
    if (!entry?.product) return;
    pushItem(entry.product, {
      matchScore:
        entry.matchScore != null ? capMatchPercent(entry.matchScore) : null,
      comparisonLabel: entry.comparisonLabel || null,
      scoreSource: SIMILAR_PRODUCTS_SOURCES.INTENT_ENGINE,
      aiRanked: false,
      showMatchScore: true,
    });
  });

  if (items.length === 0 || !hasIntent) {
    categorySimilar
      .filter((p) => getProductId(p) !== currentId)
      .forEach((p) => {
        pushItem(p, {
          matchScore: null,
          scoreSource: SIMILAR_PRODUCTS_SOURCES.CATEGORY_BROWSE,
          aiRanked: false,
          showMatchScore: false,
        });
      });
  }

  const limited = items.slice(0, limit);

  let source = SIMILAR_PRODUCTS_SOURCES.CATEGORY_BROWSE;
  if (limited.some((i) => i.scoreSource === SIMILAR_PRODUCTS_SOURCES.AI_SESSION)) {
    source = SIMILAR_PRODUCTS_SOURCES.AI_SESSION;
  } else if (
    limited.some((i) => i.scoreSource === SIMILAR_PRODUCTS_SOURCES.INTENT_ENGINE)
  ) {
    source = SIMILAR_PRODUCTS_SOURCES.INTENT_ENGINE;
  }

  return {
    source,
    contextSummary,
    items: limited,
    currentInAiSession: currentInSession,
  };
}

export default resolveSimilarProducts;
